import React, { useMemo } from 'react';
import { Row, Col, Card, Badge, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetProductsQuery } from '../slices/productsApiSlice';
import { Link } from 'react-router-dom';
import Product from '../components/Product';
import Loader from '../components/Loader';
import Message from '../components/Message';
import Paginate from '../components/Paginate';
import ProductCarousel from '../components/ProductCarousel';
import Meta from '../components/Meta';
import { useDispatch, useSelector } from 'react-redux';
import { useGetApprovedFilledShiftsQuery, useGetAvailableShiftsQuery, useGetWeeklyShiftsByWardQuery } from '../slices/shiftApiSlice';
import { useGetHospitalLocationsQuery } from '../slices/hospitalLocationApiSlice';
import { useGetPreShiftsQuery } from '../slices/preShiftApiSlice';

// ─── Simple hook to detect mobile screen width ────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return isMobile;
};

// Same localStorage keys the ward "Approve and update worked shifts" screens
// use for their "Past shifts only" toggle (admin: shiftWardList_showPastOnly,
// manager: mgr_shiftWardList_showPastOnly) — set to true before navigating so
// the card takes the user straight to the filtered, past-due view it counted.
const LS_PAST_ADMIN   = 'shiftWardList_showPastOnly';
const LS_PAST_MANAGER = 'mgr_shiftWardList_showPastOnly';

// ─── Shared manager ward-scope helper — same directorate/inclusion/own-ward
// rules used across the ward screens. Kept local to this file (rather than
// pulled from userInfo directly in each hook) so both cards below scope
// their counts identically. ─────────────────────────────────────────────
const useManagerWardScope = ({ skip }) => {
  const { userInfo } = useSelector((state) => state.auth);
  const isAdmin = userInfo?.isAdmin === true;
  const isManager = userInfo?.isManager === true;
  const isReporter = userInfo?.isReporter === true;
  const managerDirectorate = userInfo?.directorateName || null;
  const managerOnlyOwnWard = userInfo?.onlyOwnWard === true;
  const managerWardInclusions = userInfo?.wardAreaInclusions || [];

  const visible = isAdmin || isManager || isReporter;

  // Only managers need the hospital-locations lookup to work out which wards
  // fall inside their directorate/own-ward/inclusions — admin and reporter
  // both see every ward unscoped (scopeToWard passes non-managers straight
  // through below), so this query is skipped for anyone but a manager.
  const { data: hospitalLocations = [], isLoading: loadingLocations } = useGetHospitalLocationsQuery(undefined, {
    skip: skip || !visible || !isManager,
  });

  const inclusionIds = useMemo(
    () =>
      new Set(
        managerWardInclusions.map((w) => (typeof w === 'object' && w !== null ? w._id : w))
      ),
    [managerWardInclusions]
  );

  const wardToDirectorateMap = useMemo(() => {
    if (!hospitalLocations.length) return {};
    const map = {};
    hospitalLocations.forEach((loc) => {
      if (loc.wardDepartmentArea && loc.directorate) {
        map[loc.wardDepartmentArea] = loc.directorate;
      }
    });
    return map;
  }, [hospitalLocations]);

  const includedWardNames = useMemo(() => {
    if (!hospitalLocations.length || inclusionIds.size === 0) return [];
    return hospitalLocations
      .filter((loc) => inclusionIds.has(loc._id))
      .map((loc) => loc.wardDepartmentArea)
      .filter(Boolean);
  }, [hospitalLocations, inclusionIds]);

  // Filters an array of records down to this manager's ward scope, given a
  // function that pulls the ward name off each record. Admins get the array
  // back untouched.
  const scopeToWard = (records, getWard) => {
    if (isAdmin || !isManager) return records;

    if (managerOnlyOwnWard) {
      return records.filter((r) => {
        const ward = getWard(r);
        if (!ward) return false;
        return ward === userInfo?.speciality || includedWardNames.includes(ward);
      });
    }

    if (managerDirectorate) {
      return records.filter((r) => {
        const ward = getWard(r);
        if (!ward) return false;
        return wardToDirectorateMap[ward] === managerDirectorate || includedWardNames.includes(ward);
      });
    }

    if (includedWardNames.length > 0) {
      return records.filter((r) => includedWardNames.includes(getWard(r)));
    }

    return records;
  };

  return {
    visible,
    isAdmin,
    isManager,
    isReporter,
    managerDirectorate,
    managerOnlyOwnWard,
    //loadingLocations: visible && !isAdmin && loadingLocations,
    loadingLocations: visible && isManager && loadingLocations,
    scopeToWard,
  };
};

// ─── Outstanding shift-approvals count, scoped the same way the ward
// "Approve and update worked shifts" screens scope their rows: admin sees
// everything, manager sees their own ward (if onlyOwnWard) or directorate,
// plus any admin-configured ward inclusions. "Waiting for approval" = shift
// already ended (endDateTime in the past) but still sitting in the
// approvals list — the same set the "Past shifts only" toggle on that
// screen filters down to. ─────────────────────────────────────────────────
const useOutstandingApprovalsCount = () => {
  const scope = useManagerWardScope({ skip: false });
  const { visible, isAdmin, isManager, managerDirectorate, managerOnlyOwnWard, loadingLocations, scopeToWard } = scope;

  const { data: shifts, isLoading: loadingShifts } = useGetApprovedFilledShiftsQuery(undefined, {
    skip: !visible,
  });

  const count = useMemo(() => {
    if (!shifts) return 0;
    const scoped = scopeToWard(shifts, (s) => s.wardDepartmentArea);
    const now = new Date();
    return scoped.filter((s) => new Date(s.endDateTime) < now).length;
  }, [shifts, scopeToWard]);

  return {
    visible,
    isAdmin,
    isManager,
    managerDirectorate,
    managerOnlyOwnWard,
    count,
    isLoading: visible && (loadingShifts || loadingLocations),
  };
};

const ApprovalsCard = () => {
  const navigate = useNavigate();
  const { visible, isAdmin, isManager, isReporter, managerDirectorate, managerOnlyOwnWard, count, isLoading } = useOutstandingApprovalsCount();

  if (!visible) return null;

  const clickable = isAdmin || isManager; // reporter has no write screen to land on — view-only

  const handleClick = () => {
    if (!clickable) return;
    try {
      localStorage.setItem(isAdmin ? LS_PAST_ADMIN : LS_PAST_MANAGER, JSON.stringify(true));
    } catch {
      // localStorage can throw in private browsing / storage-restricted contexts — the
      // screen just falls back to its default "All shifts" view in that case.
    }
    navigate(isAdmin ? '/admin/approved-filled' : '/manager/approved-filled');
  };

  const scopeLabel = managerOnlyOwnWard
    ? ' for your ward'
    : managerDirectorate
      ? ` for ${managerDirectorate}`
      : '';

  return (
    <Card
      className="mb-4 shadow-sm"
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (clickable && (e.key === 'Enter' || e.key === ' ')) handleClick();
      }}
      style={{ cursor: clickable ? 'pointer' : 'default', borderLeft: '6px solid #fd7e14' }}
    >
      <Card.Body className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: '0.75rem' }}>
        <div>
          <Card.Title className="mb-1">Shifts Awaiting Approval</Card.Title>
          <Card.Text className="text-muted mb-0">
            Past shifts still needing their worked hours approved{isManager && !isAdmin ? scopeLabel : ''}
            {isReporter ? ' (view only)' : '. Click to review.'}
          </Card.Text>
        </div>
        {isLoading ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <Badge
            bg={count > 0 ? 'danger' : 'secondary'}
            style={{ fontSize: '1.4rem', padding: '0.5em 0.85em' }}
          >
            {count}
          </Badge>
        )}
      </Card.Body>
    </Card>
  );
};

// ─── Outstanding pre-shift-request approvals count. A pre-shift request is
// "awaiting approval" while preShiftStatus === 'Pending' (see
// preShiftController.js — approvePreShift/rejectPreShift move it to
// 'Approved'/'Rejected'). useGetPreShiftsQuery already comes back
// ward-scoped from the backend for managers (getPreShifts branches on
// req.user.isAdmin server-side), so scopeToWard here is defence-in-depth,
// same as the shift-approvals count above. ─────────────────────────────────
const useOutstandingPreShiftApprovalsCount = () => {
  const scope = useManagerWardScope({ skip: false });
  const { visible, isAdmin, isManager, managerDirectorate, managerOnlyOwnWard, loadingLocations, scopeToWard } = scope;

  const { data: preShifts, isLoading: loadingPreShifts } = useGetPreShiftsQuery(undefined, {
    skip: !visible,
  });

  const count = useMemo(() => {
    if (!preShifts) return 0;
    const scoped = scopeToWard(preShifts, (p) => p.wardDepartmentArea);
    return scoped.filter((p) => p.preShiftStatus === 'Pending').length;
  }, [preShifts, scopeToWard]);

  return {
    visible,
    isAdmin,
    isManager,
    managerDirectorate,
    managerOnlyOwnWard,
    count,
    isLoading: visible && (loadingPreShifts || loadingLocations),
  };
};

const PreShiftApprovalsCard = () => {
  const navigate = useNavigate();
  const { visible, isAdmin, isManager, isReporter, managerDirectorate, managerOnlyOwnWard, count, isLoading } = useOutstandingPreShiftApprovalsCount();

  if (!visible) return null;

  const clickable = isAdmin || isManager;

  const handleClick = () => {
    if (!clickable) return;
    navigate(isAdmin ? '/admin/ward-preshifts' : '/manager/ward-preshifts');
  };

  const scopeLabel = managerOnlyOwnWard
    ? ' for your ward'
    : managerDirectorate
      ? ` for ${managerDirectorate}`
      : '';

  return (
    <Card
      className="mb-4 shadow-sm"
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (clickable && (e.key === 'Enter' || e.key === ' ')) handleClick();
      }}
      style={{ cursor: clickable ? 'pointer' : 'default', borderLeft: '6px solid #6f42c1' }}
    >
      <Card.Body className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: '0.75rem' }}>
        <div>
          <Card.Title className="mb-1">Pre-Shift Requests Awaiting Approval</Card.Title>
          <Card.Text className="text-muted mb-0">
            New shift requests waiting for admin sign-off{isManager && !isAdmin ? scopeLabel : ''}
            {isReporter ? ' (view only)' : '. Click to review.'}
          </Card.Text>
        </div>
        {isLoading ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <Badge
            bg={count > 0 ? 'danger' : 'secondary'}
            style={{ fontSize: '1.4rem', padding: '0.5em 0.85em' }}
          >
            {count}
          </Badge>
        )}
      </Card.Body>
    </Card>
  );
};

// ─── Open Shifts grid — one row per shift "type" (a preset start/end window,
// same windows as the Ward Weekly View's presets), one column per day of the
// selected week, cell = count of shifts that day that best match that
// window. Defaults to Open (unfilled, active, lineStatus 'Open') shifts
// only; the "Show all shifts" switch swaps in every non-cancelled shift for
// the week instead (filled or not — same source as the Ward Weekly View).
// Escort is its own row: any shift flagged escort=true is counted there
// instead of by its time, regardless of what time it actually runs. A shift
// whose time doesn't land exactly on a preset (e.g. 10:00-16:00) is
// bucketed into whichever preset's start+end is closest to it — see
// pickBucketKey below. ───────────────────────────────────────────────────
const SHIFT_TYPE_BUCKETS = [
  { key: 'LD', label: 'Long Day', start: '08:00', end: '20:00' },
  { key: 'ND', label: 'Night Duty', start: '20:00', end: '08:00' },
  { key: 'AM', label: 'Morning', start: '08:00', end: '16:00' },
  { key: 'PM', label: 'Afternoon', start: '14:00', end: '20:00' },
  { key: 'TW', label: 'Twilight', start: '20:00', end: '00:00' },
  { key: 'ELD', label: 'Extended Long Day', start: '07:30', end: '20:30' },
];
const ESCORT_ROW = { key: 'ESCORT', label: 'Escort' };
const OPEN_SHIFTS_GRID_ROWS = [...SHIFT_TYPE_BUCKETS, ESCORT_ROW];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const timeToMinutes = (t) => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

// Total minutes of "distance" between a shift's start/end and a preset's
// start/end, unwrapping both across midnight where needed (Night Duty /
// Twilight) so an overnight shift compares sensibly against them.
const bucketDistance = (shiftStartMin, shiftEndMin, bucket) => {
  const bStart = timeToMinutes(bucket.start);
  let bEnd = timeToMinutes(bucket.end);
  if (bEnd <= bStart) bEnd += 1440;

  let sEnd = shiftEndMin;
  if (sEnd <= shiftStartMin) sEnd += 1440;

  return Math.abs(shiftStartMin - bStart) + Math.abs(sEnd - bEnd);
};

const pickBucketKey = (startTime, endTime) => {
  const sMin = timeToMinutes(startTime);
  const eMin = timeToMinutes(endTime);
  let best = SHIFT_TYPE_BUCKETS[0];
  let bestDist = Infinity;
  SHIFT_TYPE_BUCKETS.forEach((bucket) => {
    const dist = bucketDistance(sMin, eMin, bucket);
    if (dist < bestDist) {
      bestDist = dist;
      best = bucket;
    }
  });
  return best.key;
};

const toLocalISO = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Monday of the week containing `date` (matches the Mon-start week the Ward
// Weekly View itself uses).
const getWeekStart = (date) => {
  const current = new Date(date);
  current.setHours(0, 0, 0, 0);
  const day = current.getDay();
  const diff = current.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(current);
  monday.setDate(diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

const OpenShiftsGrid = () => {
  const navigate = useNavigate();
  // Same directorate/inclusion/own-ward scoping the other Home cards use —
  // admin sees every ward unscoped, a manager gets narrowed to theirs.
  const { isAdmin, isManager, managerDirectorate, managerOnlyOwnWard, scopeToWard } =
    useManagerWardScope({ skip: false });
  const canView = isAdmin || isManager;

  const [weekStart, setWeekStart] = React.useState(() => getWeekStart(new Date()));
  // Client-side filter — the underlying query already returns only Open,
  // unfilled shifts for the week, so this just narrows that same set down
  // to the ones also flagged allowAgencyView, without a separate request.
  const [agencyViewOnly, setAgencyViewOnly] = React.useState(false);
  // Default stays Open shifts only, as before. Flipping this swaps the data
  // source to every (non-cancelled) shift for the week — filled or not —
  // rather than filtering the Open-only list further, since a filled shift
  // was never in that list to begin with.
  const [showAllShifts, setShowAllShifts] = React.useState(false);

  const weekDays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        return { date: toLocalISO(d), label: DAY_LABELS[i] };
      }),
    [weekStart]
  );

  const startDate = toLocalISO(weekStart);
  const endDate = weekDays[6]?.date || startDate;

  const { data: openShifts, isLoading: isLoadingOpen } = useGetAvailableShiftsQuery(
    { startDate, endDate },
    { skip: !canView || showAllShifts }
  );

  // No `ward` passed — every ward, every (non-cancelled) shift in range,
  // same source the Ward Weekly View itself uses. Narrowed to the manager's
  // own wards below (scopeToWard), same as everywhere else on this screen.
  const { data: allShifts, isLoading: isLoadingAll } = useGetWeeklyShiftsByWardQuery(
    { startDate, endDate },
    { skip: !canView || !showAllShifts }
  );

  const isLoading = showAllShifts ? isLoadingAll : isLoadingOpen;
  const shiftsForGrid = showAllShifts ? allShifts : openShifts;

  // "Agency-visible only" means something different depending on the mode:
  // in Open-shifts mode there's nothing to check but allowAgencyView (a
  // shift can't be agency-*filled* while still Open/unfilled). In All-shifts
  // mode, most agency shifts were assigned directly by an admin and were
  // never released to the portal at all, so allowAgencyView alone would
  // hide almost all of them — matches the same "agency shift" definition
  // getAgencyShifts() uses: filledByAgency or actualFilledByAgency set.
  const isAgencyShift = (shift) =>
    shift.allowAgencyView === true || !!shift.filledByAgency || !!shift.actualFilledByAgency;

  const grid = useMemo(() => {
    const counts = {};
    OPEN_SHIFTS_GRID_ROWS.forEach((row) => {
      counts[row.key] = {};
    });

    scopeToWard(shiftsForGrid || [], (s) => s.wardDepartmentArea)
      .filter((shift) => !agencyViewOnly || isAgencyShift(shift))
      .forEach((shift) => {
      const start = new Date(shift.startDateTime);
      const end = new Date(shift.endDateTime);
      const dayKey = toLocalISO(start);
      const startTime = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
      const endTime = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;

      const bucketKey = shift.escort ? ESCORT_ROW.key : pickBucketKey(startTime, endTime);
      counts[bucketKey][dayKey] = (counts[bucketKey][dayKey] || 0) + 1;
    });

    return counts;
  }, [shiftsForGrid, agencyViewOnly, scopeToWard]);

  // Per-row totals (across the week), per-day totals (down the rows), and
  // the overall week total — all derived from the same `grid` counts rather
  // than recomputed from openShifts, so they always agree with what's on
  // screen (including the agency-visible-only filter above).
  const rowTotals = useMemo(() => {
    const totals = {};
    OPEN_SHIFTS_GRID_ROWS.forEach((row) => {
      totals[row.key] = weekDays.reduce((sum, day) => sum + (grid[row.key]?.[day.date] || 0), 0);
    });
    return totals;
  }, [grid, weekDays]);

  const dayTotals = useMemo(() => {
    const totals = {};
    weekDays.forEach((day) => {
      totals[day.date] = OPEN_SHIFTS_GRID_ROWS.reduce((sum, row) => sum + (grid[row.key]?.[day.date] || 0), 0);
    });
    return totals;
  }, [grid, weekDays]);

  const weekTotal = useMemo(
    () => Object.values(rowTotals).reduce((sum, n) => sum + n, 0),
    [rowTotals]
  );

  if (!canView) return null;

  // Any Mon-Sun cell/header takes you to the Ward Weekly View for this same
  // week — the grid is one week at a time, so which column was clicked
  // doesn't change the destination.
  const goToWeek = () => {
    navigate(isAdmin ? '/admin/ward-weekly-schedule' : '/manager/ward-weekly-schedule', {
      state: { weekDate: startDate },
    });
  };

  const shiftWeek = (deltaWeeks) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + deltaWeeks * 7);
    setWeekStart(getWeekStart(d));
  };

  const isThisWeek = startDate === toLocalISO(getWeekStart(new Date()));

  return (
    <Card className="mb-4 shadow-sm">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-3" style={{ gap: '0.75rem' }}>
          <Card.Title className="mb-0">
            {showAllShifts ? 'All Shifts' : 'Open Shifts'}
            {isManager && !isAdmin && (
              <small
                className="text-muted ms-2"
                style={{ fontWeight: 400 }}
                title="Scoped to your directorate (plus any admin-assigned inclusions) — same scope as the rest of this screen"
              >
                {managerOnlyOwnWard ? 'my ward' : managerDirectorate || 'no directorate assigned'}
              </small>
            )}
          </Card.Title>
          <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
            <div className="form-check form-switch mb-0 me-1" title="Show every shift this week (filled or not), instead of just Open ones">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="showAllShiftsSwitch"
                checked={showAllShifts}
                onChange={(e) => setShowAllShifts(e.target.checked)}
              />
              <label className="form-check-label small text-muted" htmlFor="showAllShiftsSwitch">
                Show all shifts
              </label>
            </div>
            <div
              className="form-check form-switch mb-0 me-1"
              title={
                showAllShifts
                  ? 'Show only shifts an agency is filling or has filled, or that are flagged visible to agencies'
                  : 'Show only Open shifts flagged as visible to agencies'
              }
            >
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="agencyViewOnlySwitch"
                checked={agencyViewOnly}
                onChange={(e) => setAgencyViewOnly(e.target.checked)}
              />
              <label className="form-check-label small text-muted" htmlFor="agencyViewOnlySwitch">
                {showAllShifts ? 'Agency shifts only' : 'Agency-visible only'}
              </label>
            </div>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => shiftWeek(-1)}>
              ‹ Prev
            </button>
            <span className="text-muted small">
              {startDate} – {endDate}{isThisWeek ? ' (This Week)' : ''}
            </span>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => shiftWeek(1)}>
              Next ›
            </button>
            {!isThisWeek && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => setWeekStart(getWeekStart(new Date()))}
              >
                This Week
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-sm table-bordered mb-0" style={{ minWidth: 560 }}>
              <thead>
                <tr>
                  <th style={{ width: '18%' }}>Shift Type</th>
                  {weekDays.map((day) => (
                    <th
                      key={day.date}
                      role="button"
                      tabIndex={0}
                      style={{ cursor: 'pointer', textAlign: 'center' }}
                      onClick={goToWeek}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') goToWeek(); }}
                      title="Open the Ward Weekly View for this week"
                    >
                      {day.label}
                    </th>
                  ))}
                  <th style={{ textAlign: 'center' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {OPEN_SHIFTS_GRID_ROWS.map((row) => (
                  <tr key={row.key}>
                    <td className="fw-semibold">{row.label}</td>
                    {weekDays.map((day) => {
                      const count = grid[row.key]?.[day.date] || 0;
                      return (
                        <td
                          key={day.date}
                          role="button"
                          tabIndex={0}
                          style={{
                            cursor: 'pointer',
                            textAlign: 'center',
                            backgroundColor: count > 0 ? '#e7f1ff' : undefined,
                          }}
                          onClick={goToWeek}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') goToWeek(); }}
                        >
                          {count}
                        </td>
                      );
                    })}
                    <td className="fw-semibold" style={{ textAlign: 'center' }}>
                      {rowTotals[row.key] || 0}
                    </td>
                  </tr>
                ))}
                <tr className="table-light fw-semibold">
                  <td>Total</td>
                  {weekDays.map((day) => (
                    <td key={day.date} style={{ textAlign: 'center' }}>
                      {dayTotals[day.date] || 0}
                    </td>
                  ))}
                  <td style={{ textAlign: 'center' }}>{weekTotal}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

// ─── Logged-in staff member's own shift summary. Reuses the exact same
// useGetApprovedFilledShiftsQuery as ApprovalsCard above (RTK Query dedupes
// the two calls into one request) — that query already returns only shifts
// that are filled (staff or agency), stage1Approval true, isActive, not
// agencyCancelled, and not yet finalised (actualStatus not Complete/
// Not-Worked). Splitting THOSE shifts by whether filledBy is this user and
// whether endDateTime has passed gives exactly the two buckets asked for:
//   - Waiting for Approval: assigned to me, already ended, admin/manager
//     hasn't recorded Worked/Not-Worked yet.
//   - Upcoming Shifts: assigned to me, end time hasn't passed yet.
// ──────────────────────────────────────────────────────────────────────────
const useMyShiftsSummary = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const myId = userInfo?._id ? String(userInfo._id) : null;

  const { data: shifts, isLoading } = useGetApprovedFilledShiftsQuery(undefined, {
    skip: !myId,
  });

  const { waitingForApproval, upcoming } = useMemo(() => {
    if (!shifts || !myId) return { waitingForApproval: 0, upcoming: 0 };

    const now = new Date();
    let waiting = 0;
    let upcomingCount = 0;

    shifts.forEach((s) => {
      const filledById = s.filledBy && typeof s.filledBy === 'object' ? s.filledBy._id : s.filledBy;
      if (!filledById || String(filledById) !== myId) return;

      if (new Date(s.endDateTime) < now) {
        waiting += 1;
      } else {
        upcomingCount += 1;
      }
    });

    return { waitingForApproval: waiting, upcoming: upcomingCount };
  }, [shifts, myId]);

  return {
    visible: !!myId,
    isLoading: !!myId && isLoading,
    waitingForApproval,
    upcoming,
  };
};

const MyShiftsCard = () => {
  const navigate = useNavigate();
  const { visible, isLoading, waitingForApproval, upcoming } = useMyShiftsSummary();

  if (!visible) return null;

  const goToRoster = () => navigate('/my-roster');
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') goToRoster();
  };

  const tileStyle = (accent, bg) => ({
    cursor: 'pointer',
    border: `1px solid ${accent}`,
    backgroundColor: bg,
  });

  return (
    <Card className="mb-4 shadow-sm">
      <Card.Body>
        <Card.Title className="mb-3">My Shifts</Card.Title>
        <Row className="g-3">
          <Col xs={6}>
            <div
              role="button"
              tabIndex={0}
              onClick={goToRoster}
              onKeyDown={handleKeyDown}
              className="p-3 rounded d-flex flex-column align-items-center text-center"
              style={tileStyle('#ffc107', '#fff8e1')}
            >
              {isLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <div style={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1 }}>{waitingForApproval}</div>
              )}
              <div className="text-muted small mt-1">Waiting for Approval</div>
            </div>
          </Col>
          <Col xs={6}>
            <div
              role="button"
              tabIndex={0}
              onClick={goToRoster}
              onKeyDown={handleKeyDown}
              className="p-3 rounded d-flex flex-column align-items-center text-center"
              style={tileStyle('#0d6efd', '#e7f1ff')}
            >
              {isLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <div style={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1 }}>{upcoming}</div>
              )}
              <div className="text-muted small mt-1">Upcoming Shifts</div>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

const HomeScreen = () => {
  const { pageNumber, keyword } = useParams();
  const { userInfo } = useSelector((state) => state.auth);
  const isMobile = useIsMobile();

  const accessType = userInfo?.isAdmin
    ? 'Admin'
    : userInfo?.isManager
      ? 'Manager'
      : 'User';

  const { data, isLoading, error } = useGetProductsQuery({
    keyword,
    accessType,
  });

  return (
    <>
      {!keyword ? (
        // ── Only show carousel on tablet/desktop (768px and above) ──
        isLoading ? null : error ? null : !isMobile && (
          <ProductCarousel products={data?.products || []} />
        )
      ) : (
        <Link to='/' className='btn btn-light mb-4'>
          Go Back
        </Link>
      )}

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant='danger'>
          {error?.data?.message || error.error}
        </Message>
      ) : (
        <>

          <h1>Select a Menu Option</h1>
          <Meta />
          <Row className="g-3">
            <Col xs={12} md={6}>
              <ApprovalsCard />
            </Col>
            <Col xs={12} md={6}>
              <PreShiftApprovalsCard />
            </Col>
          </Row>
          <OpenShiftsGrid />
          <MyShiftsCard />
          
          <Row className='mx-0'>
            {data?.products?.map((product) => (
              <Col
                key={product._id}
                sm={12}
                md={6}
                lg={4}
                xl={3}
                className='px-0'
              >
                <Product product={product} />
              </Col>
            ))}
          </Row>
          <Paginate
            pages={data?.pages}
            page={data?.page}
            keyword={keyword ? keyword : ''}
          />
        </>
      )}
    </>
  );
};

export default HomeScreen;